/**
 * JourneyDetailPage — the celestial path.
 *
 * URL: /journeys/:slug
 *
 * Shows the full arc of a journey as an organic constellation of dots,
 * grouped by phase. Each dot is a day:
 *   - completed → solid gold, faint glow
 *   - current   → larger, breathing glow
 *   - future    → soft cream, dim, tap-to-peek (preview only)
 *   - mock      → small ring marker, distinct
 *
 * Reusable: any journey schema with `phases` and `total_days` will render.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "../../components/journeys/journeyTheme";
import AbsorptionArc from "../../components/journeys/AbsorptionArc";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Organic offset positions (deterministic per index) so the path breathes.
function pathOffset(index) {
  // small sine-shaped sway, clamped, with a faint random-looking jitter
  const sway = Math.sin(index * 0.65) * 18;
  const jitter = ((index * 37) % 9) - 4; // -4..+4 px
  return sway + jitter;
}

export default function JourneyDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [journey, setJourney] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${API}/api/journeys/${slug}`, { headers: auth() }).then((r) =>
        r.ok ? r.json() : Promise.reject(r.status)
      ),
      fetch(`${API}/api/journeys/${slug}/progress`, { headers: auth() })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([j, p]) => {
        if (cancelled) return;
        setJourney(j);
        setProgress(p);
      })
      .catch((e) => !cancelled && setError(typeof e === "number" ? "Journey not found" : "Could not load"));
    return () => { cancelled = true; };
  }, [slug]);

  const currentDay = progress?.current_day || 1;
  const dayState = (n) => {
    if (n < currentDay) return "completed";
    if (n === currentDay) return "current";
    return "future";
  };

  // Build phase → days view; phases come ordered from the API.
  const phaseGroups = useMemo(() => {
    if (!journey?.phases) return [];
    const daysIdx = journey.days_index || [];
    const dayMeta = Object.fromEntries(daysIdx.map((d) => [d.day_number, d]));
    return journey.phases.map((ph) => ({
      ...ph,
      day_objs: (ph.days || []).map((n) => ({
        day_number: n,
        ...(dayMeta[n] || {}),
      })),
    }));
  }, [journey]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: P.bgWash }}>
        <div>
          <p className="text-[14px]" style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_HEAD, fontStyle: "italic" }}>
            {error}
          </p>
          <button
            onClick={() => navigate("/journeys")}
            className="mt-4 text-[11px] tracking-[0.3em] uppercase"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
          >
            ← back
          </button>
        </div>
      </div>
    );
  }

  if (!journey) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: P.bgWash }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.accentBlue }} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-32 relative"
      style={{ background: P.bgWash }}
      data-testid="journey-detail-page"
    >
      {/* ── Hero image (top 40%) on navy ─────────────────────────── */}
      <div
        className="relative w-full"
        style={{ height: "44vh", maxHeight: 360 }}
      >
        <img
          src={`${API}/api/journey-images/${journey.slug}/day1_road_types.png`}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover", filter: "saturate(0.85) contrast(0.95) brightness(0.78)" }}
        />
        {/* Bottom navy gradient for legibility */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0.55) 60%, rgba(13,27,42,1) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(13,27,42,0.35) 0%, transparent 50%)",
          }}
        />
      </div>

      <button
        onClick={() => navigate("/journeys")}
        className="fixed top-4 left-4 z-50 p-2 rounded-full"
        style={{ background: "rgba(255,255,255,0.10)", border: `1px solid ${P.hairline}`, backdropFilter: "blur(10px)" }}
        data-testid="journey-detail-back-btn"
        aria-label="Back to journeys"
      >
        <ArrowLeft size={16} style={{ color: P.textSecondary }} />
      </button>

      <div className="px-6 max-w-md mx-auto -mt-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.42em] mb-3"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            {journey.subtitle || "Journey"}
          </p>
          <h1
            className="text-[34px] leading-[1.08] mb-4"
            style={{
              color: P.textPrimary,
              fontFamily: JOURNEY_FONT_HEAD,
              fontWeight: 400,
              letterSpacing: "-0.012em",
            }}
          >
            {journey.title}
          </h1>
          <p
            className="text-[14.5px] leading-[1.7] mb-10 whitespace-pre-line"
            style={{
              color: P.textSecondary,
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 400,
            }}
          >
            {/* Break long single-paragraph descriptions into pairs of lines */}
            {(journey.description || "")
              .replace(/\.\s+/g, ".\n\n")
              .replace(/—\s+/g, "—\n")}
          </p>
        </motion.div>

        {/* Continue CTA — softer gold gradient + glow */}
        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          whileTap={{ scale: 0.985 }}
          whileHover={{ scale: 1.01 }}
          onClick={() => navigate(`/journeys/${slug}/day/${currentDay}`)}
          className="w-full rounded-full py-[15px] mb-12 flex items-center justify-center gap-2 relative"
          style={{
            background: `linear-gradient(135deg, ${P.accentBlue} 0%, ${P.accentBlueDeep} 100%)`,
            color: "#FFFFFF",
            fontFamily: JOURNEY_FONT_BODY,
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "0.01em",
            border: "1px solid rgba(77,163,255,0.55)",
            boxShadow:
              "0 12px 30px rgba(46,123,214,0.40), 0 0 0 1px rgba(255,255,255,0.10) inset, 0 1px 0 rgba(255,255,255,0.20) inset, 0 0 24px rgba(77,163,255,0.30)",
          }}
          data-testid="journey-continue-btn"
        >
          <Sparkles size={14} />
          {progress ? `Continue — Day ${currentDay}` : `Begin — Day ${currentDay}`}
        </motion.button>

        {/* Celestial path */}
        <div className="space-y-12" data-testid="celestial-path">
          {phaseGroups.map((phase, pi) => (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + pi * 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p
                className="text-[10px] uppercase tracking-[0.4em] mb-5 text-center"
                style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
              >
                {phase.name}
              </p>

              <div className="relative flex flex-col items-center gap-6">
                {/* faint connecting line */}
                <div
                  className="absolute left-1/2 top-2 bottom-2 w-px -translate-x-1/2"
                  style={{ background: "linear-gradient(to bottom, transparent, rgba(77,163,255,0.25), transparent)" }}
                />

                {phase.day_objs.map((d, di) => {
                  const state = dayState(d.day_number);
                  const offset = pathOffset(d.day_number);
                  const isMock = d.title?.toLowerCase().startsWith("mock");
                  return (
                    <motion.button
                      key={d.day_number}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.25 + pi * 0.1 + di * 0.03 }}
                      whileTap={{ scale: 0.94 }}
                      onClick={() => navigate(`/journeys/${slug}/day/${d.day_number}`)}
                      className="relative flex items-center gap-4 px-3 py-2 rounded-2xl"
                      style={{
                        marginLeft: offset,
                        background: state === "current" ? "rgba(255,255,255,0.06)" : "transparent",
                        border: state === "current" ? `1px solid ${P.hairline}` : "1px solid transparent",
                        backdropFilter: state === "current" ? "blur(10px)" : "none",
                      }}
                      data-testid={`day-dot-${d.day_number}`}
                    >
                      {/* Dot — gold for COMPLETED (achievement only), blue for current */}
                      <span
                        className="relative inline-block flex-shrink-0"
                        style={{
                          width: state === "current" ? 16 : 11,
                          height: state === "current" ? 16 : 11,
                          borderRadius: 999,
                          background:
                            state === "completed"
                              ? `linear-gradient(135deg, ${P.accentGold}, #B8995A)`
                              : state === "current"
                              ? `radial-gradient(circle, #FFFFFF 0%, ${P.accentBlue} 90%)`
                              : "rgba(255,255,255,0.18)",
                          border: isMock ? `1.5px solid ${P.accentBlue}` : "none",
                          boxShadow:
                            state === "current"
                              ? "0 0 22px rgba(77,163,255,0.65), 0 0 6px rgba(255,255,255,0.9)"
                              : state === "completed"
                              ? "0 0 8px rgba(212,184,126,0.42)"
                              : "none",
                        }}
                      >
                        {state === "current" && (
                          <motion.span
                            className="absolute inset-0 rounded-full"
                            style={{ background: P.accentBlue, opacity: 0.35 }}
                            animate={{ scale: [1, 1.8, 1], opacity: [0.35, 0, 0.35] }}
                            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                      </span>

                      {/* Label */}
                      <div className="flex-1 text-left">
                        <p
                          className="text-[10.5px] tracking-[0.32em] uppercase"
                          style={{
                            color:
                              state === "completed"
                                ? P.accentGold
                                : state === "current"
                                ? P.textPrimary
                                : P.textMuted,
                            fontFamily: JOURNEY_FONT_LABEL,
                            fontWeight: 500,
                          }}
                        >
                          Day {d.day_number}
                        </p>
                        <p
                          className="text-[15px] leading-tight mt-0.5"
                          style={{
                            color:
                              state === "future"
                                ? P.textMuted
                                : state === "current"
                                ? P.textPrimary
                                : P.textSecondary,
                            fontFamily: JOURNEY_FONT_HEAD,
                            fontWeight: 400,
                            letterSpacing: "-0.005em",
                            opacity: state === "future" ? 0.6 : 1,
                          }}
                        >
                          {d.title || `Day ${d.day_number}`}
                          {isMock && (
                            <span
                              className="ml-2 text-[9px] tracking-[0.3em] uppercase"
                              style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
                            >
                              · mock
                            </span>
                          )}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mock test entry — only when journey supports it */}
        {journey.supports_mock_test && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16"
          >
            <button
              onClick={() => navigate(`/journeys/${slug}/mock`)}
              className="w-full rounded-[20px] p-5 flex items-center justify-between"
              style={{
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(22px) saturate(1.2)",
                WebkitBackdropFilter: "blur(22px) saturate(1.2)",
                border: `1px solid ${P.hairline}`,
              }}
              data-testid="journey-mock-entry-btn"
            >
              <div className="text-left">
                <p
                  className="text-[10px] uppercase tracking-[0.4em] mb-1"
                  style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
                >
                  Recognition check
                </p>
                <p
                  className="text-[16px]"
                  style={{ color: P.textPrimary, fontFamily: JOURNEY_FONT_HEAD }}
                >
                  See what's already familiar
                </p>
              </div>
              <Sparkles size={18} style={{ color: P.accentBlue }} />
            </button>
            <AbsorptionArc slug={slug} totalDays={journey.total_days || 21} />
          </motion.div>
        )}

        <p
          className="text-[11px] tracking-[0.3em] uppercase text-center mt-16"
          style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL }}
        >
          {journey.verify_before_launch
            ? "verify before launch · content under review"
            : "21 days · gentle, daily"}
        </p>
      </div>
    </div>
  );
}
